import React from 'react';
import { motion } from 'framer-motion';
import backgroundImage from '../assets/image1.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { ArrowDown, Download, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Downloads = () => {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 via-black/90 to-yellow-500/20" />
      </div>
      
      {/* Animated floating orbs - yellow theme */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-yellow-300 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
      <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-amber-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse" />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center items-center px-4 py-20">
        {/* Animated badge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-2 bg-yellow-400/10 backdrop-blur-lg px-4 py-2 rounded-full mb-8 border border-yellow-400/20"
        >
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-medium text-yellow-200">{t('downloads.badge')}</span>
        </motion.div>

        {/* Main Title */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl md:text-7xl font-bold text-center mb-4"
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-400">
            {t('downloads.title')}
          </span>
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-xl text-gray-300 text-center mb-12 max-w-2xl"
        >
          {t('downloads.subtitle')}
        </motion.p>

        {/* Download Cards Container */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-full max-w-4xl"
        >
          <div className="bg-black/60 backdrop-blur-lg rounded-2xl p-8 border border-yellow-500/20 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Android Card */}
              <motion.div 
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl blur-xl" />
                <div className="relative bg-gray-900/80 backdrop-blur-sm border border-yellow-500/20 rounded-2xl p-8 h-full hover:border-yellow-400/40 transition-all">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-400 rounded-2xl flex items-center justify-center transform group-hover:rotate-6 transition-transform">
                      <FontAwesomeIcon icon={faAndroid} className="w-10 h-10 text-black" />
                    </div>
                  </div>
                  
                  <h2 className="text-white text-3xl font-bold text-center mb-4">{t('downloads.android.title')}</h2>
                  
                  <div className="text-center mb-6">
                    <p className="text-gray-400 mb-2">{t('downloads.android.latest_version')}</p>
                    <p className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-400">
                      2.3.4
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-4">{t('downloads.android.requirements')}</p>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.confesz.confeszapp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="clickable inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-400 hover:from-yellow-300 hover:to-amber-300 text-black font-bold py-3 px-8 rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-yellow-400/20"
                    >
                      <Download className="w-5 h-5" />
                      {t('downloads.android.button')}
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* iOS Card */}
              <motion.div 
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl blur-xl" />
                <div className="relative bg-gray-900/80 backdrop-blur-sm border border-yellow-500/20 rounded-2xl p-8 h-full hover:border-yellow-400/40 transition-all">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-400 rounded-2xl flex items-center justify-center transform group-hover:rotate-6 transition-transform">
                      <FontAwesomeIcon icon={faApple} className="w-10 h-10 text-black" />
                    </div>
                  </div>
                  
                  <h2 className="text-white text-3xl font-bold text-center mb-4">{t('downloads.ios.title')}</h2>
                  
                  <div className="text-center mb-6">
                    <p className="text-gray-400 mb-2">{t('downloads.ios.latest_version')}</p>
                    <p className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-400">
                      2.3.4
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-4">{t('downloads.ios.requirements')}</p>
                    <a
                      href="https://apps.apple.com/us/app/confesz/id6541248711"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="clickable inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-400 hover:from-yellow-300 hover:to-amber-300 text-black font-bold py-3 px-8 rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-yellow-400/20"
                    >
                      <Download className="w-5 h-5" />
                      {t('downloads.ios.button')}
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center text-gray-400 text-sm border-t border-yellow-500/20 pt-6">
              <p>{t('downloads.footer')}</p>
            </div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <ArrowDown className="w-6 h-6 animate-bounce text-yellow-400/60" />
        </motion.div>
      </div>
    </div>
  );
};

export default Downloads;